/**
 * podiumMath.ts — the PURE trajectory maths behind the tour-podium celebration
 * (M94). Kept three.js-free and unit-tested: the cinematic scripts each ball's
 * "over the edge → ground bounce → up into the trophy cradle" flight from
 * these closed-form solutions, so the ball lands EXACTLY in the cup for any
 * podium height — no physics tuning, no misses.
 */

export interface CupArcSpec {
  /** Podium platform height the ball rolls off (its centre starts at h + ballR). */
  h: number;
  /** Ball radius (the ground contact height). */
  ballR: number;
  /** Where the ball leaves the platform's front edge (z, course units). */
  edgeZ: number;
  /** Forward speed the push gives the ball at the edge. */
  vz0: number;
  /** The trophy cradle's position (ball centre when seated). */
  cupZ: number;
  cupY: number;
  /** Gravity (positive number). */
  g: number;
  /** How long the bounce-to-cup arc takes (the feel knob). */
  t2: number;
}

export interface CupArc {
  /** Time from the edge to the ground contact. */
  t1: number;
  /** Where the ground bounce happens. */
  z1: number;
  /** Launch velocity of the bounce arc (solved so it ENDS at the cradle). */
  vy2: number;
  vz2: number;
  t2: number;
  /** Peak height of the bounce arc (>= cupY: the ball rises past the rim, then drops in). */
  apexY: number;
}

/**
 * Solve the two-segment flight. Segment A: free fall off the edge (vy 0,
 * vz `vz0`) until the ball touches the ground. Segment B: a bounce arc from
 * the contact point that lands the ball centre exactly on the cradle after
 * `t2` seconds — velocities derived from the kinematics, never guessed.
 */
export function solveCupArc(spec: CupArcSpec): CupArc {
  const drop = Math.max(0.01, spec.h); // centre falls from h+ballR to ballR
  const t1 = Math.sqrt((2 * drop) / spec.g);
  const z1 = spec.edgeZ + spec.vz0 * t1;
  const t2 = Math.max(0.15, spec.t2);
  const vy2 = (spec.cupY - spec.ballR + 0.5 * spec.g * t2 * t2) / t2;
  const vz2 = (spec.cupZ - z1) / t2;
  // Apex of segment B (relative to the ground contact height).
  const tApex = Math.min(t2, Math.max(0, vy2 / spec.g));
  const apexY = spec.ballR + vy2 * tApex - 0.5 * spec.g * tApex * tApex;
  return { t1, z1, vy2, vz2, t2, apexY };
}

/** Segment-A height at `t` since the edge (before the ground contact). */
export function fallY(spec: CupArcSpec, t: number): number {
  return spec.h + spec.ballR - 0.5 * spec.g * t * t;
}

/** Segment-B position at `t` since the bounce (0..t2). */
export function bounceAt(spec: CupArcSpec, arc: CupArc, t: number): { y: number; z: number } {
  return {
    y: spec.ballR + arc.vy2 * t - 0.5 * spec.g * t * t,
    z: arc.z1 + arc.vz2 * t,
  };
}
